/* eslint-disable max-lines -- if you need to modify this code, fix this first */
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Deferred } from '@sama/js-core';
import { times } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { lastValueFrom, merge, Observable, Subject } from 'rxjs';
import {
  delayWhen,
  filter,
  mergeMap,
  share,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  assetPath,
  AssetResource,
} from '../../../cross-team-shared/misc-services/resources/asset-resource.service';
import {
  maxAssetLoadingRetries,
  UrlRequestService,
} from '../../../cross-team-shared/misc-services/resources/url-request.service';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { LoadTaskResult } from '../../../team-apis/content/content-connector';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { PointCloudManifest } from '../../../team-apis/otterdam/asset-manifests';
import { SegmentationMaskResource } from '../../resources/segmentation-mask-resource.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { SensorFrame3D } from '../../state/sensor-renderer-state';

import {
  AccumulatedGroundAttributes,
  CloudAttributes,
  CloudAttributesService,
} from '../kd-tree/cloud-attributes.service';
import { AssetLoadError } from '../misc-utils';
import { PointCloudAssetEventService } from './point-cloud-asset-event.service';

export interface LoadedFrameInfo {
  sensorFrame: SensorFrame3D;
  frameNumber: number;
  retryCount: number;
}

@Injectable()
export class PointCloudAssetService
  extends InjectableSuperclass
  implements OnDestroy
{
  private cloudAttributes = new Array<CloudAttributes>();

  private frameLoaded$: Observable<LoadedFrameInfo | { error: Error }>;
  private reset$ = new Subject<void>();
  private manifest$ = new Subject<PointCloudManifest>();

  private shellConnector: ShellConnector;
  private loadTaskTrigger$ = new Subject<Deferred<LoadTaskResult>>();
  private accumulatedGroundAttributes?: AccumulatedGroundAttributes;

  constructor(
    private assetResource: AssetResource,
    private cloudAttributesService: CloudAttributesService,
    private pointCloudAssetEventService: PointCloudAssetEventService,
    private segmentationMaskResource: SegmentationMaskResource,
    private store: PointCloudContentStore,
    private tokenManagementService: TokenManagementService,
    private urlRequestService: UrlRequestService,
    injector: Injector,
  ) {
    super();
    this.shellConnector = injector.get(shellConnectorToken);

    this.frameLoaded$ = this.buildFrameLoaded$();

    this.subscribeToLoadTaskTriggers();
    this.subscribeToFrameLoaded();
  }

  ngOnDestroy() {
    this.store('sensorRendererState').delete();
  }

  loadTask() {
    this.store('status').set('loading');
    this.reset$.next();

    const deferred = new Deferred<LoadTaskResult>();
    this.loadTaskTrigger$.next(deferred);
    return deferred.promise;
  }

  getAsset(frameNumber: number): CloudAttributes | undefined {
    return this.cloudAttributes[frameNumber];
  }

  getAccumulatedGroundAsset() {
    return this.accumulatedGroundAttributes;
  }

  private updateStoreWithFrame(
    loadedFrame: LoadedFrameInfo | { error: Error },
  ) {
    if ('error' in loadedFrame) {
      this.pointCloudAssetEventService.handleLoadFailure(loadedFrame.error);
      this.reset$.next();
      return;
    }
    const { frameNumber, sensorFrame } = loadedFrame;
    this.pointCloudAssetEventService.handleFrameLoadSuccess(frameNumber);

    this.store('sensorRendererState')('sensorFrames')(frameNumber).set(
      sensorFrame,
    );
    this.shellConnector.setFrameLoaded(frameNumber);
  }

  private async loadAsset() {
    const url = this.store.getInputUrl();
    if (!url) {
      throw new AssetLoadError('No asset url given');
    }
    return this.startPerFrameLoading(url);
  }

  /**
   * This should do all the asynchronous work needed for fetching assets,
   * without modifying the store. Another task may load before fetching is done,
   * so updating the store with info from these assets would be a bug.
   */
  private async startPerFrameLoading(url: string) {
    const manifest = await this.loadManifest(url);

    if (manifest.cloud_asset_ids.length === 0) {
      throw new AssetLoadError('No frames were provided');
    }
    this.manifest$.next(manifest);

    await Promise.all([
      this.buildFirstFramePromise(),
      this.loadGroundAsset(manifest),
    ]);

    const emptyUrls = manifest.cloud_asset_ids.map(() => undefined);
    return { sensorFrames: emptyUrls };
  }

  private async loadManifest(manifestUrl: string) {
    if (this.store.state().task.assets_archived_at !== null) {
      throw new AssetLoadError(
        'Asset for this output has been archived. Task input and output answers are still retrievable through task export and API',
      );
    }

    const token = await this.getAssetsToken();
    return this.urlRequestService.request<PointCloudManifest>(
      'GET',
      manifestUrl,
      {
        headers: { Authorization: 'Bearer ' + token },
      },
    );
  }

  private buildFirstFramePromise() {
    return lastValueFrom(
      this.frameLoaded$.pipe(
        take(1),
        switchMap(async (frame) => {
          if ('error' in frame) {
            this.reset$.next();
            throw frame.error;
          }
          return frame;
        }),
      ),
    );
  }

  private getAssetsToken() {
    return this.tokenManagementService.getToken('assets');
  }

  private subscribeToLoadTaskTriggers() {
    let start: number;
    this.subscribeTo(
      this.loadTaskTrigger$.pipe(
        switchMap(async (deferred) => {
          try {
            start = performance.now();
            const asset = await this.loadAsset();
            return { asset, deferred };
          } catch (error) {
            console.error(error);
            return { error };
          }
        }),
      ),
      (result) => {
        if ('error' in result) {
          this.pointCloudAssetEventService.handleLoadFailure(
            result.error,
            start,
          );
          this.reset$.next();
        } else {
          result.deferred.resolve(
            this.pointCloudAssetEventService.handleLoadSuccess(result.asset),
          );
        }
      },
    );
  }

  private subscribeToFrameLoaded() {
    this.subscribeTo(
      this.reset$.pipe(
        switchMap(() =>
          this.frameLoaded$.pipe(
            delayWhen(() =>
              this.store('status').$.pipe(
                filter((status) => status === 'ready'),
              ),
            ),
          ),
        ),
      ),
      this.updateStoreWithFrame,
    );
  }

  private buildFrameLoaded$() {
    return this.reset$.pipe(
      startWith(undefined),
      switchMap(() =>
        this.manifest$.pipe(
          tap((manifest) =>
            this.pointCloudAssetEventService.initPerFrameEventTracking(
              manifest,
            ),
          ),
          mergeMap((manifest) => merge(...this.loadAllFrames(manifest))),
          tap((frame) =>
            this.pointCloudAssetEventService.trackLoadedFrame(frame),
          ),
        ),
      ),
      share(),
    );
  }

  private loadAllFrames(manifest: PointCloudManifest) {
    return times(manifest.cloud_asset_ids.length, async (frameNumber) =>
      this.loadFrame(manifest, frameNumber).catch((error) => ({
        error,
      })),
    );
  }

  private async loadFrame(manifest: PointCloudManifest, frameNumber: number) {
    const assetId = manifest.cloud_asset_ids[frameNumber];
    const [asset, segmentationMask] = await Promise.all([
      this.loadPointCloudAsset(frameNumber, assetId),
      this.loadSegmentationMask(frameNumber),
    ]);

    this.cloudAttributes[frameNumber] = this.cloudAttributesService.build(
      asset.response,
      segmentationMask.response,
    );

    return {
      sensorFrame: { cloudUrl: '' },
      frameNumber,
      retryCount: asset.retryCount + segmentationMask.retryCount,
    };
  }

  private loadPointCloudAsset(frameNumber: number, assetId: string) {
    return this.shellConnector.queueAssetRequest(frameNumber, async () =>
      this.assetResource.getAsset(assetId, await this.getAssetsToken()),
    );
  }

  private async loadGroundAsset(manifest: PointCloudManifest) {
    const accumulatedGround = manifest.accumulated_ground;

    if (this.store.state().output.meta.enable_accumulated_ground) {
      if (accumulatedGround) {
        try {
          const token = await this.getAssetsToken();
          const asset = await this.urlRequestService.downloadResource(
            assetPath(accumulatedGround),
            {
              headers: { 'X-Kaguya-Token': 'Bearer ' + token },
              responseType: 'arraybuffer',
              retryCount: maxAssetLoadingRetries,
            },
          );
          this.accumulatedGroundAttributes =
            this.cloudAttributesService.buildAccumulatedGround(asset);
        } catch (error) {
          throw new AssetLoadError(
            'Accumulated ground asset failed to load, please reload',
          );
        }
      } else {
        throw new AssetLoadError(
          'Accumulated ground asset not found, please reupload task',
        );
      }
    }
  }

  private async loadSegmentationMask(frameNumber: number) {
    return this.shellConnector.queueAssetRequest(frameNumber, async () =>
      this.segmentationMaskResource.getSegmentationMask(
        this.store.state().project.id,
        this.store.state().task.id,
        this.store.state().output.id,
        frameNumber,
        await this.getAssetsToken(),
      ),
    );
  }
}
